import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TeamEntity } from '@shared/models/team.models';
import { Tag } from '@shared/models/tags.models';
import { selectAllTeams } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import {
  selectFollowingTeams,
  selectFollowingWorkGroups
} from '@features/whereabouts/store/followingTeamsAndTags.duck';
import { scopeKey, WhereaboutsScope } from '@features/whereabouts/models/whereaboutsScope';

export interface ScopedGroups {
  teams: TeamEntity[];
  workGroups: Tag[];
}

const NO_TEAMS: TeamEntity[] = [];
const NO_WORKGROUPS: Tag[] = [];

// The team/workgroup entities a whereabouts view should show for the given
// scope. For a pinned scope the entity is looked up in the company-wide lists;
// until those load a placeholder with an empty name is returned so dependent
// loaders can still fire by id.
export function useScopedGroups(scope: WhereaboutsScope): ScopedGroups {
  const followedTeams = useSelector(selectFollowingTeams);
  const followedWorkGroups = useSelector(selectFollowingWorkGroups);
  const allTeams = useSelector(selectAllTeams);
  const allTags = useSelector(selectAllTags);
  const key = scopeKey(scope);

  const scopedTeam = useMemo<TeamEntity | undefined>(() => {
    if (scope.kind !== 'team') return undefined;
    const found = (allTeams ?? []).find(t => t.id === scope.teamId) as TeamEntity | undefined;
    return found ?? ({ id: scope.teamId, name: '' } as TeamEntity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, allTeams]);

  const scopedWorkGroup = useMemo<Tag | undefined>(() => {
    if (scope.kind !== 'workgroup') return undefined;
    const found = (allTags ?? []).find(t => t.id === scope.workGroupId) as Tag | undefined;
    return found ?? ({ id: scope.workGroupId, label: '', companyId: '' } as Tag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, allTags]);

  const teams = useMemo(() => {
    if (scope.kind === 'followed') return followedTeams;
    return scopedTeam ? [scopedTeam] : NO_TEAMS;
  }, [scope.kind, followedTeams, scopedTeam]);

  const workGroups = useMemo(() => {
    if (scope.kind === 'followed') return followedWorkGroups;
    return scopedWorkGroup ? [scopedWorkGroup] : NO_WORKGROUPS;
  }, [scope.kind, followedWorkGroups, scopedWorkGroup]);

  return useMemo(() => ({ teams, workGroups }), [teams, workGroups]);
}
